/**
 * The ceremony's card as triangles.
 *
 * A real box with a rounded outline and a bevelled border, in object space:
 * the face spans x ∈ [−1, 1], y ∈ [−A, A] (A is the deck's aspect) and the
 * stock is `HALF_THICK` either side of z = 0. The bevel and the thickness are
 * geometry, not a texture trick, which is the whole point — it is what lets the
 * card go **edge-on** during the flip and show a gilt hairline instead of
 * vanishing, and what gives the border a sharp specular to catch.
 *
 * The solid is convex and wound counter-clockwise from outside, so the shader
 * culls back faces and no depth buffer or triangle sort is needed: for a convex
 * body the front-facing triangles ARE the visible ones. (Blockari's cube had to
 * sort because its ornaments made it concave; a card has no ornaments.)
 *
 * Per vertex: position (3), normal (3), uv (2), side (1 — see `SIDE`).
 */

import { CARD } from '@/pixi/layout';

/** Which surface a vertex belongs to: the print, the back, or the stock's edge. */
export const SIDE = { face: 0, back: 1, edge: 2 } as const;

/** Half-height in units where the half-width is 1. */
export const HALF_HEIGHT = CARD.height / CARD.width;
/** Corner radius in the same units. */
export const CORNER = (CARD.cornerRadius / CARD.width) * 2;
/** Half the stock's thickness. A shade thicker than a real card: this is a plate. */
export const HALF_THICK = 0.04;
/** How far the bevel runs in from the outline, in the same units. */
export const BEVEL = 0.013;

export interface CardGeometryData {
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly uvs: Float32Array;
  readonly sides: Float32Array;
  readonly indices: Uint16Array;
}

class Builder {
  pos: number[] = [];
  nor: number[] = [];
  uv: number[] = [];
  side: number[] = [];
  idx: number[] = [];

  vert(p: [number, number, number], n: [number, number, number], uv: [number, number], side: number): number {
    this.pos.push(p[0], p[1], p[2]);
    const len = Math.hypot(n[0], n[1], n[2]) || 1;
    this.nor.push(n[0] / len, n[1] / len, n[2] / len);
    this.uv.push(uv[0], uv[1]);
    this.side.push(side);
    return this.pos.length / 3 - 1;
  }

  tri(a: number, b: number, c: number): void {
    this.idx.push(a, b, c);
  }

  build(): CardGeometryData {
    return {
      positions: new Float32Array(this.pos),
      normals: new Float32Array(this.nor),
      uvs: new Float32Array(this.uv),
      sides: new Float32Array(this.side),
      indices: new Uint16Array(this.idx),
    };
  }
}

interface OutlinePoint {
  x: number;
  y: number;
  /** Outward normal in the xy plane. */
  nx: number;
  ny: number;
}

/**
 * The rounded outline, counter-clockwise, at an inset from the card's edge.
 * `corner` segments per corner, so a card is `4 * (corner + 1)` points.
 */
export function outline(inset: number, corner = 7): OutlinePoint[] {
  const hw = 1 - inset;
  const hh = HALF_HEIGHT - inset;
  const r = Math.max(0.0001, CORNER - inset);
  const out: OutlinePoint[] = [];
  // Corner centres, counter-clockwise from the bottom right.
  const centres: Array<[number, number, number]> = [
    [hw - r, -(hh - r), -Math.PI / 2],
    [hw - r, hh - r, 0],
    [-(hw - r), hh - r, Math.PI / 2],
    [-(hw - r), -(hh - r), Math.PI],
  ];
  for (const [cx, cy, from] of centres) {
    for (let s = 0; s <= corner; s++) {
      const a = from + (Math.PI / 2) * (s / corner);
      const nx = Math.cos(a);
      const ny = Math.sin(a);
      out.push({ x: cx + nx * r, y: cy + ny * r, nx, ny });
    }
  }
  return out;
}

/** The print's uv for a point on the card, in the face texture's own 0..1 space. */
function faceUV(x: number, y: number, mirror: boolean): [number, number] {
  const u = (x + 1) / 2;
  return [mirror ? 1 - u : u, (HALF_HEIGHT - y) / (2 * HALF_HEIGHT)];
}

/**
 * Build the card. `corner` segments per rounded corner (7 is plenty at the
 * sizes a ceremony draws at, and the whole card is then 224 triangles).
 */
export function cardGeometry(corner = 7): CardGeometryData {
  const b = new Builder();
  const capRing = outline(BEVEL, corner); // the flat part of the face
  const edgeRing = outline(0, corner); // the widest point of the stock
  const n = capRing.length;
  const capZ = HALF_THICK;
  const edgeZ = HALF_THICK - BEVEL;

  for (const side of [SIDE.face, SIDE.back] as const) {
    const sign = side === SIDE.face ? 1 : -1;
    const mirror = side === SIDE.back;
    // The cap: a fan from the centre.
    const centre = b.vert([0, 0, capZ * sign], [0, 0, sign], faceUV(0, 0, mirror), side);
    const cap: number[] = capRing.map((p) =>
      b.vert([p.x, p.y, capZ * sign], [0, 0, sign], faceUV(p.x, p.y, mirror), side),
    );
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      if (sign > 0) b.tri(centre, cap[i]!, cap[j]!);
      else b.tri(centre, cap[j]!, cap[i]!);
    }
    // The bevel: cap ring out to the widest point, its normal halfway between
    // the face and the side, which is what catches the border's glint.
    const bevel: number[] = edgeRing.map((p) =>
      b.vert(
        [p.x, p.y, edgeZ * sign],
        [p.nx * 0.72, p.ny * 0.72, sign * 0.7],
        faceUV(p.x, p.y, mirror),
        side,
      ),
    );
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      if (sign > 0) {
        b.tri(cap[i]!, bevel[i]!, bevel[j]!);
        b.tri(cap[i]!, bevel[j]!, cap[j]!);
      } else {
        b.tri(cap[i]!, bevel[j]!, bevel[i]!);
        b.tri(cap[i]!, cap[j]!, bevel[j]!);
      }
    }
  }

  // The stock's side, between the two bevels: the gilt hairline the flip shows.
  const top: number[] = edgeRing.map((p) =>
    b.vert([p.x, p.y, edgeZ], [p.nx, p.ny, 0], [0.5, 0], SIDE.edge),
  );
  const bottom: number[] = edgeRing.map((p) =>
    b.vert([p.x, p.y, -edgeZ], [p.nx, p.ny, 0], [0.5, 1], SIDE.edge),
  );
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    b.tri(top[i]!, bottom[i]!, bottom[j]!);
    b.tri(top[i]!, bottom[j]!, top[j]!);
  }

  return b.build();
}
